"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import type { TocItem } from "@/lib/markdown/toc";
import { twMerge } from "tailwind-merge";

type Props = {
  items: TocItem[];
  className?: string;
  /**
   * Show subsections (h3) only under the section being read. Keeps a long
   * chapter's list to its main sections while still exposing the fine
   * structure of the current one.
   */
  nested?: boolean;
};

/** Headings at or above this viewport offset count as "being read". */
const READING_LINE = 140;

/**
 * Server-provided heading list with a client scroll-spy: the last heading
 * that has scrolled past the reading line is the active one, so exactly one
 * entry is highlighted at every scroll position. When the list itself
 * scrolls (a long chapter inside a viewport-capped sticky rail), the active
 * entry is kept in view by moving only the list's own scroll position.
 */
const TableOfContents: React.FC<Props> = ({
  items,
  className,
  nested = false,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);

  // Section (h2) each entry belongs to; an h2 belongs to itself.
  const sectionOf = useMemo(() => {
    const map = new Map<string, string | null>();
    let current: string | null = null;

    for (const item of items) {
      if (item.level === 2) {
        current = item.id;
      }
      map.set(item.id, current);
    }

    return map;
  }, [items]);

  const activeSection = activeId ? (sectionOf.get(activeId) ?? null) : null;
  const visibleItems = nested
    ? items.filter(
        (item) => item.level === 2 || sectionOf.get(item.id) === activeSection,
      )
    : items;

  useEffect(() => {
    const headings = items
      .map((item) => document.getElementById(item.id))
      .filter((element): element is HTMLElement => element !== null);

    if (headings.length === 0) {
      return;
    }

    let frame = 0;

    const update = () => {
      frame = 0;
      let current = headings[0];

      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= READING_LINE) {
          current = heading;
        } else {
          break;
        }
      }

      setActiveId(current.id);
    };

    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [items]);

  useEffect(() => {
    const nav = navRef.current;

    if (!nav || !activeId || nav.scrollHeight <= nav.clientHeight) {
      return;
    }

    const active = Array.from(nav.querySelectorAll<HTMLAnchorElement>("a")).find(
      (anchor) => anchor.dataset.tocId === activeId,
    );

    if (!active) {
      return;
    }

    const padding = 12;
    const top = active.offsetTop;
    const bottom = top + active.offsetHeight;

    if (top < nav.scrollTop + padding) {
      nav.scrollTop = top - padding;
    } else if (bottom > nav.scrollTop + nav.clientHeight - padding) {
      nav.scrollTop = bottom - nav.clientHeight + padding;
    }
  }, [activeId]);

  return (
    <nav
      ref={navRef}
      className={twMerge("google-anno-skip relative flex flex-col gap-0.5", className)}
    >
      {visibleItems.map((item) => (
        <a
          key={item.id}
          href={`#${item.id}`}
          data-toc-id={item.id}
          aria-current={activeId === item.id ? "location" : undefined}
          className={twMerge(
            "rounded-lg px-3 py-1.5 text-sm transition-colors hover:bg-muted hover:text-foreground",
            item.level === 3 && "pl-7 text-[13px]",
            activeId === item.id
              ? "bg-secondary/60 font-semibold text-foreground"
              : "text-muted-foreground",
          )}
        >
          {item.text}
        </a>
      ))}
    </nav>
  );
};

export default TableOfContents;
